import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  TCI_ENV_CTRL_ACTION_TYPE,
  ITCIAppEnvControlProviderRequest,
  ITCIApplicationDeployRequest,
  TCITenantSelectorType,
  TCIApplicationDeploymentMode,
  ITCIVpnConnectionBrief,
  IDSVpnConnection,
  ITCIApplicationDeployResponse,
  ITCIApplication,
  USER_ROLES
} from "@tci/common";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../../../ng-app-details-menu";
import { AbstractTCITenantComponent } from "../../../../../ng-tenant/tci-tenant-component";
import { BaseSubmenuAppVpnService } from "./base-submenu-app-vpn.service";
import { NgSecurityService } from "../../../../../ng-shared/ng-services/ng-security.service";
import { SelectItem } from "primeng/primeng";
import { Observable, Subscription } from "rxjs";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_ENV_APP_VPN.toString(),
    templateUrl: "./base-submenu-app-vpn.component.html",
    styleUrls: ["./base-submenu-app-vpn.component.less"]
})
export class BaseSubmenuAppVpnComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {

    vpnList: SelectItem[] = [];
    selectedVPNConnectionId = undefined;
    appVpnConnectionId = null;
    disableField = false;
    currentVPN: IDSVpnConnection;
    appStatus: ITCIApplicationDeployResponse;
    isRORoleUser = false;
    noVpn = "No VPN";
    isShowDropdownList = false;
    isLoadingVPNStatus = false;
    isVPNStatus = false;
    app: ITCIApplication;
    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    constructor(private appVpnService: BaseSubmenuAppVpnService,
                private security: NgSecurityService) {
        super();
    }

    ngOnInit(): void {
        this.app = this.input.application;
        this.appVpnConnectionId = this.app.vpnConnectionId;
        this.initVPNList();

        // Request current state
        const stateRequest: ITCIApplicationDeployRequest = {
            mode: TCIApplicationDeploymentMode.MODE_APP_DETAILS,
            appId: this.app.id,
            application: this.app
        };

        this.subscriptions.push(
            this.appVpnService
                .getEnvCtrlService()
                .getTenantDeployProvider(this.input.tenantAppType)
                .isBusy(stateRequest)
                .subscribe(busy => {
                    this.disableField = busy;
                    this.isLoadingVPNStatus = this.disableField || !this.appStatus;
                    // Note: when busy is false, scale app is done, selected an VPN, call getAppLiveStatus to update vpn status.
                    if (!this.disableField && !this.isLoadingVPNStatus && !!this.currentVPN && this.selectedVPNConnectionId) {
                        this.pollingAppLiveStatus();
                    }
                })
        );

        this.subscriptions.push(this.appVpnService.getEnvCtrlService().getAppStatusSubject()
            .subscribe(res => {
                this.appStatus = res;
                this.isLoadingVPNStatus = this.disableField || !this.appStatus;
                this.isVPNStatus = this.getVpnStatus();
            } ));

        this.subscriptions.push(this.appVpnService.getEnvCtrlService().getStatusSubject()
            .subscribe((res: TCI_ENV_CTRL_ACTION_TYPE) => {
                if (res === TCI_ENV_CTRL_ACTION_TYPE.REFRESH) {
                    // Note: when get refresh event, need to reset selectedVPNConnectionId value and get app status back
                    this.selectedVPNConnectionId = undefined;
                    // Note: after push updates, application VPN connectionId is changed, but this.input.application data is old
                    // Need to get app live detail data and refresh the app status.
                    this.getAppLiveDetail();
                }
            }));

        const thisObj = this;
        this.appVpnService.getEnvCtrlService().getUserAuthForEnv()
            .subscribe(userAuth => {
                thisObj.isRORoleUser = (userAuth.role === USER_ROLES.RO);
            });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    getVpnStatus(): boolean {
        return this.appStatus
          && this.appStatus.vpnInstances !== undefined
          && this.appStatus.vpnInstances === this.appStatus.desiredInstances;
    }

    generateReqInput(): ITCIAppEnvControlProviderRequest {
        return {
            actionType: TCI_ENV_CTRL_ACTION_TYPE.APP_VPN,
            appId: this.app.id,
            application: this.app,
            tenantType: this.app.type
        };
    }

    toggleDropdown() {
        this.isShowDropdownList = !this.isShowDropdownList;
    }

    updateVpnChange(vpnCon) {
        this.selectedVPNConnectionId = vpnCon;
        this.appVpnService.getEnvCtrlService().setTabData(TCI_ENV_CTRL_ACTION_TYPE.APP_VPN, this.selectedVPNConnectionId);
        this.toggleDropdown();
        if (this.selectedVPNConnectionId) {
            this.initVPNDetail(this.selectedVPNConnectionId);
        } else {
            this.currentVPN = null;
            this.noVpn = "No VPN";
        }
    }

    initVPNList(isRefreshVPNStatus: boolean = true) {
        this.vpnList = [{
            label: "No VPN",
            value: null
        }];
        this.appVpnService.getEnvCtrlService().initAppVPNConnection(this.generateReqInput())
            .subscribe(
                res => {
                    res.vpnList.forEach((item: ITCIVpnConnectionBrief) => {
                        this.vpnList.push({
                            label: item.vpnConnectionName,
                            value: item.id
                        });
                    });
                    if (this.appVpnConnectionId) {
                        this.initVPNDetail(this.appVpnConnectionId, isRefreshVPNStatus);
                    } else {
                        this.isLoadingVPNStatus = false;
                        this.currentVPN = null;
                        this.noVpn = "No VPN";
                    }
                });
    }

    initVPNDetail(vpnConnectionId: string, isRefreshVPNStatus: boolean = true) {
        this.appVpnService.getEnvCtrlService().getVpnConnectionById(vpnConnectionId)
            .subscribe(res => {
                this.currentVPN = res;
                this.selectedVPNConnectionId = res.vpnConnectionId; // Note: auto select current vpn
                if (isRefreshVPNStatus) {
                    this.getAppLiveStatus();
                }
            }, error => {
                this.currentVPN = null;
                this.isLoadingVPNStatus = false;
                this.selectedVPNConnectionId = undefined;
                // IPAS-16071: If app has been attached with VPN, even the VPN has been deleted, still show app VPN status.
                if (isRefreshVPNStatus) {
                    this.getAppLiveStatus();
                }
                this.noVpn = "VPN connection was deleted, restart to disconnect";
            });
    }
    pollingAppLiveStatus() {
        let repeatTime = 0;
        const maxWaitPollingTime = 5, pollingInterval = 5000;
        // Note: every 5 seconds call getAppLiveStatus API, until desiredInstances = instances = vpnInstances
        this.subscriptions.push(Observable
            .timer(0, pollingInterval)
            .flatMap(() => this.appVpnService.getEnvCtrlService().getAppLiveStatus(this.app.id))
            .do(() => {
                repeatTime ++;
                this.appVpnService.getNgZone().run(() => {
                    // Note: when repeatTime reach to maxWaitPollingTime, will return false, will return current vpn status
                    // but will keep polling until desiredInstances = instances = vpnInstances, or leave current vpn tab
                    this.isLoadingVPNStatus = repeatTime < maxWaitPollingTime;
                });
            })
            .filter(data => data.desiredInstances === data.instances && data.desiredInstances === data.vpnInstances)
            .take(1)
            .subscribe(res => {
                this.updateVPNStatus(res);
            }));
    }

    getAppLiveStatus() {
        this.subscriptions.push(this.appVpnService.getEnvCtrlService().getAppLiveStatus(this.app.id)
            .subscribe(
                res => {
                    this.updateVPNStatus(res);
                },
                error => {
                    this.isLoadingVPNStatus = false;
                }));
    }

    getAppLiveDetail() {
        this.subscriptions.push(this.appVpnService.getEnvCtrlService().getAppLiveDetail(this.app.id, this.app.type)
            .subscribe(
                res => {
                    this.app = res;
                    this.appVpnConnectionId = res.vpnConnectionId;
                    this.initVPNList(false);
                }));
    }

    private updateVPNStatus(appStatus: ITCIApplicationDeployResponse) {
        // Note: use ngZone from service to avoid tenant update their component
        // use ngZone to trigger page re-rendering
        // https://stackoverflow.com/questions/41990171/angular-2-not-re-rendering-until-i-click-the-page
        this.appVpnService.getNgZone().run(() => {
            this.isLoadingVPNStatus = false;
            this.appStatus = appStatus;
            this.isVPNStatus = this.getVpnStatus();
        });
    }
}
